import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import {
  AccountsApi,
  CardsApi,
  UsersApi,
  RequestStrategyEnum,
  type AddUserAccountsRequest,
  type AddUserCardsRequest,
  type CreateUserRequest,
  type DeleteUserAccountsRequest,
  type DeleteUserCardsRequest,
  type UsersRequest,
  type UserResponse,
  type UserWithPaymentInfo,
  type WithSignal,
} from '@shared/api';

let abortControllers: AbortController[] = [];

const getUsersWithoutPermissionEffect = createEffect(async ({ signal, ...request }: WithSignal<UsersRequest> = {}) => {
  const data = await UsersApi.getUsers(request, signal);
  return { total: data.total, data: data.data?.map((user) => ({ ...user, accounts: [], cards: [] })) || [] };
});
const getUsersEffect = createEffect(({ signal, ...request }: WithSignal<UsersRequest> = {}) => UsersApi.getUsers(request, signal));
const getPaymentInfosEffect = createEffect(async (data: { users: UserResponse[] | undefined; total: number }) => {
  const users = data.users || [];
  const usersWithPaymentInfo = await Promise.all(
    users.map(async (user) => {
      const cardAbortController = new AbortController();
      const accountAbortController = new AbortController();
      abortControllers.push(cardAbortController);
      abortControllers.push(accountAbortController);
      const [cardsData, accountsData] = await Promise.all([
        CardsApi.getCards({ AssignedUserId: user.id }, cardAbortController.signal),
        AccountsApi.getAccounts({ AssignedUserId: user.id }, accountAbortController.signal),
      ]);
      return { ...user, cards: cardsData.data || [], accounts: accountsData.data || [] };
    }),
  );
  return { total: data.total, data: usersWithPaymentInfo };
});
const addUserEffect = createEffect(async (request: { user: CreateUserRequest; params: UsersRequest }) => {
  const user = await UsersApi.addUser(request.user);
  return { params: request.params, user: user.data };
});
const getPaymentInfoEffect = createEffect(async (data: { user: UserResponse | undefined; params: UsersRequest }) => {
  const user = data.user as UserResponse;
  const cardAbortController = new AbortController();
  const accountAbortController = new AbortController();
  abortControllers.push(cardAbortController);
  abortControllers.push(accountAbortController);
  const [cardsData, accountsData] = await Promise.all([
    CardsApi.getCards({ AssignedUserId: user.id }, cardAbortController.signal),
    AccountsApi.getAccounts({ AssignedUserId: user.id }, accountAbortController.signal),
  ]);
  return { ...user, cards: cardsData.data || [], accounts: accountsData.data || [] };
});
const addAccountEffect = createEffect(async (request: AddUserAccountsRequest) => {
  const user = await UsersApi.addUserAccounts(request);
  return { ...request, user: user.data };
});
const getAccountInfoEffect = createEffect(async (request: AddUserAccountsRequest) => {
  // TODO: Remove delay after fixing on backend asynchronous account addition
  const delay = new Promise((res) => setTimeout(() => res(''), 3000));
  await delay;
  const abortController = new AbortController();
  abortControllers.push(abortController);
  return AccountsApi.getAccounts({ AssignedUserId: request.userIds }, abortController.signal);
});
const addCardEffect = createEffect(async (request: AddUserCardsRequest) => {
  const user = await UsersApi.addUserCards(request);
  return { ...request, user: user.data };
});
const getCardInfoEffect = createEffect(async (request: AddUserCardsRequest) => {
  // TODO: Remove delay after fixing on backend asynchronous card addition
  const delay = new Promise((res) => setTimeout(() => res(''), 3000));
  await delay;
  const abortController = new AbortController();
  abortControllers.push(abortController);
  const card = await CardsApi.getCards({ AssignedUserId: request.userIds }, abortController.signal);
  return { ...request, data: card.data };
});
const removeAccountEffect = createEffect(async (request: DeleteUserAccountsRequest) => UsersApi.deleteUserAccounts(request));
const removeCardEffect = createEffect((request: DeleteUserCardsRequest) => UsersApi.deleteUserCards(request));
const clearEvent = createEvent();

sample({
  source: getUsersEffect.doneData,
  fn: (data) => ({ total: data.total, users: data.data }),
  filter: (data) => data.data?.length !== 0,
  target: getPaymentInfosEffect,
});
sample({ source: addUserEffect.doneData, target: getPaymentInfoEffect });
sample({ source: addAccountEffect.doneData, target: getAccountInfoEffect, fn: ({ userIds, accountIds }) => ({ userIds, accountIds }) });
sample({ source: addCardEffect.doneData, target: getCardInfoEffect });

const $dataStore = createStore<{ total: number; data: UserWithPaymentInfo[] }>({
  total: 0,
  data: [],
})
  .on(getUsersWithoutPermissionEffect.done, (current, data) => (data.result ? { total: data.result.total, data: data.result.data || [] } : current))
  .on(getPaymentInfosEffect.done, (current, data) => (data.result ? { total: data.result.total, data: data.result.data || [] } : current))
  .on(getPaymentInfoEffect.done, (current, data) => ({
    total: current.total + 1,
    data: current.data.length >= data.params.params.Count! ? current.data : [...current.data, data.result],
  }))
  .on(getAccountInfoEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userIds) return { ...user, accounts: data.result.data || [] };
      return user;
    }),
  }))
  .on(getCardInfoEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userIds) return { ...user, cards: data.result.data || [] };
      return user;
    }),
  }))
  .on(removeAccountEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userIds) return { ...user, accounts: user.accounts.filter((account) => account.id !== data.params.accountIds[0]) };
      return user;
    }),
  }))
  .on(removeCardEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userIds) return { ...user, cards: user.cards.filter((card) => card.cardId !== data.params.cardIds[0]) };
      return user;
    }),
  }))
  .reset(clearEvent);

const $initialLoadingStore = createStore<boolean>(false)
  .on(getUsersWithoutPermissionEffect.pending, (_, data) => data)
  .on(getUsersWithoutPermissionEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getUsersWithoutPermissionEffect.done, () => false)
  .on(getUsersEffect.pending, (_, data) => data)
  .on(getUsersEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getPaymentInfosEffect.pending, (_, data) => data)
  .on(getPaymentInfosEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getPaymentInfosEffect.done, () => false)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(addUserEffect.pending, (_, data) => data)
  .on(addUserEffect.fail, () => false)
  .on(addUserEffect.done, () => false)
  .on(getPaymentInfoEffect.pending, (_, data) => data)
  .on(getPaymentInfoEffect.fail, () => false)
  .on(getPaymentInfoEffect.done, () => false)
  .on(getAccountInfoEffect.pending, (_, data) => data)
  .on(getAccountInfoEffect.fail, () => false)
  .on(getAccountInfoEffect.done, () => false)
  .on(getCardInfoEffect.pending, (_, data) => data)
  .on(getCardInfoEffect.fail, () => false)
  .on(getCardInfoEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUsersWithoutPermissionEffect, () => null)
  .on(getUsersWithoutPermissionEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getUsersWithoutPermissionEffect.done, () => null)
  .on(getUsersEffect, () => null)
  .on(getUsersEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getUsersEffect.done, () => null)
  .on(getPaymentInfosEffect, () => null)
  .on(getPaymentInfosEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getPaymentInfosEffect.done, () => null)
  .on(getPaymentInfoEffect, () => null)
  .on(getPaymentInfoEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getPaymentInfoEffect.done, () => null)
  .on(getAccountInfoEffect, () => null)
  .on(getAccountInfoEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getAccountInfoEffect.done, () => null)
  .on(getCardInfoEffect, () => null)
  .on(getCardInfoEffect.fail, () => 'Something went wrong while fetching Users. Please try again.')
  .on(getCardInfoEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(addUserEffect, () => null)
  .on(addUserEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while adding User. Please try again.').message)
  .on(addUserEffect.done, () => null)
  .on(addAccountEffect, () => null)
  .on(
    addAccountEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while adding Account. Please try again.').message,
  )
  .on(addAccountEffect.done, () => null)
  .on(addCardEffect, () => null)
  .on(addCardEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while adding Card. Please try again.').message)
  .on(addCardEffect.done, () => null)
  .on(removeAccountEffect, () => null)
  .on(
    removeAccountEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while removing Account. Please try again.').message,
  )
  .on(removeAccountEffect.done, () => null)
  .on(removeCardEffect, () => null)
  .on(removeCardEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while removing Card. Please try again.').message)
  .on(removeCardEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUsersWithoutPermissionApi = ApiUtils.wrapperApi(getUsersWithoutPermissionEffect, RequestStrategyEnum.Abort);
const getUsersApi = ApiUtils.wrapperApi(getUsersEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => {
  getUsersWithoutPermissionApi.cancel();
  getUsersApi.cancel();
  abortControllers.forEach((abortController) => abortController.abort());
  abortControllers = [];
});

export const $usersStore = {
  events: {
    getUsersWithoutPermission: getUsersWithoutPermissionApi.trigger,
    getUsers: getUsersApi.trigger,
    addUser: addUserEffect,
    addAccount: addAccountEffect,
    addCard: addCardEffect,
    removeAccount: removeAccountEffect,
    removeCard: removeCardEffect,
    clear: clearEvent,
  },
  store: combine({
    initialLoading: $initialLoadingStore,
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
